import {
  Box,
  Flex,
  toast
} from '@sparrowengg/twigs-react';
import React, { useEffect, useState } from 'react';
import {
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  useForm
} from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import AppDetails from './components/app-details';
import AppAuthentication from './components/app-authentication';
import AppEventConfiguration from './components/app-event-configuration';
import { WebhookStateValues } from 'features/apps/add-apps/types';
import { useSelector, useDispatch } from 'react-redux';
import { IRootDispatch, IRootState } from 'commons/types';
import { devPortalServices } from 'commons/services';
import { clearWebhookApp, setWebhookApp } from 'redux-store/actions';
import { defaultValues } from '../constants';
import { Spinner } from 'commons/components/spinner';

const AddWebhook = () => {
  const navigate = useNavigate();
  const {
    getValues,
    setValue,
    handleSubmit,
    register,
    reset,
    trigger,
    watch,
    formState: { dirtyFields, errors }
  } = useForm({
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues
  });
  const [currentConfig, setCurrentConfig] = useState('appDetails');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const app = useSelector((state: IRootState) => state?.app);
  const dispatch: IRootDispatch = useDispatch();
  const [btnLoader, setBtnLoader] = useState(false);
  const [loader, setLoader] = useState(true);

  const webhookOnSubmitHandler = async (webhookDetails: any) => {
    try {
      const formData = new FormData();
      formData.append('appType', 'WEBHOOK');
      if (app?.authentication) {
        Object.keys(dirtyFields).forEach((key) => {
          if (key) {
            if (key === 'events') {
              webhookDetails[key] = webhookDetails[key]?.map((event: any) => ({ id: event?.id, name: event?.name }));
            }
            if (key === 'properties') {
              Object.keys(
                webhookDetails['properties']['headers'] || {}
              ).forEach((header) => {
                !webhookDetails['properties']['headers'][header] &&
                  delete webhookDetails['properties']['headers'][header];
              });
            }
            formData.append(
              key,
              typeof webhookDetails[key] === 'object' && key !== 'icon'
                ? JSON.stringify(webhookDetails[key])
                : webhookDetails[key]
            );
          }
        });
        if(!webhookDetails.icon){
          formData.delete("icon");
          formData.append("isIconDeleted",true as any);
        }
        else{
          formData.append("isIconDeleted",false as any);
        }
        formData.append('webhookId', webhookDetails?.webhookId);
        app?.validated && formData.delete('isValidated');
        await devPortalServices.updateWebhookApp(formData);
        toast({
          variant: 'success',
          description: 'Webhook app has been updated successfully'
        });
      } else {
        for (const key in webhookDetails) {
          if (webhookDetails.hasOwnProperty(key) && key !== 'webhookId') {
            if (key === 'events') {
              webhookDetails[key] = webhookDetails[key]?.map((event: any) => ({ id: event?.id, name: event?.name }));
            }
            if (key === 'properties') {
              Object.keys(
                webhookDetails['properties']['headers'] || {}
              ).forEach((header) => {
                !webhookDetails['properties']['headers'][header] &&
                  delete webhookDetails['properties']['headers'][header];
              });
            }
            formData.append(
              key,
              typeof webhookDetails[key] === 'object' && key !== 'icon'
                ? JSON.stringify(webhookDetails[key])
                : webhookDetails[key]
            );
          }
          if (key === 'icon' && !webhookDetails[key]?.name) {
            formData.delete('icon');
          }
        }
        await devPortalServices.uploadWebhookApp(formData);
        toast({
          variant: 'success',
          description: `Subscribed to ${
            webhookDetails?.events?.length || getValues('events')?.length
          } Event(s) Successfully`
        });
      }
      reset(defaultValues);
      dispatch(clearWebhookApp);
      navigate('/?tab=Webhook');
    } catch (err: any) {
      console.error(err);
      if (err?.response?.status === 400) {
        toast({
          variant: 'error',
          description:
            err?.response?.data?.error ||
            'Error while saving web hook. Try again later or contact support.'
        });
      } else {
        toast({
          variant: 'error',
          description:
            'Error while saving web hook. Try again later or contact support.'
        });
      }
    } finally {
      setBtnLoader(false);
    }
  };

  const resetValues = (app: any) => {
    const properties = app?.properties;
    return {
      appName: app?.name,
      shortDescription: app?.description,
      supportEmail: app?.supportEmail,
      supportUrl: app?.supportUrl,
      websiteLink: app?.websiteLink,
      secretKey: app?.secretKey,
      url: app?.url,
      events: app?.events,
      isValidated: app?.validated,
      icon: {
        data: app.icon
      },
      authentication: app?.authentication,
      properties: {
        headers: { ...properties?.headers } || {}
      },
      webhookId: app?.id as number
    };
  };
  const fetchCurrentWebhook = async (id: string) => {
    try {
      const response = await devPortalServices.getCurrentWebhook(parseInt(id));
      reset(resetValues(response));
      dispatch(setWebhookApp(response));
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (app?.authentication) {
      reset(resetValues(app));
    }
    if (queryParams?.get('id')) {
      setLoader(true);
      fetchCurrentWebhook(queryParams?.get('id') as string);
    } else {
      setLoader(false);
    }
    if (queryParams?.get('page') === 'authenticate') {
      setCurrentConfig('appAuthentication');
    }
  }, []);

  const [currentURL, setCurrentURL] = useState(app?.url);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (!!app?.authentication) {
      if (
        watch('url') === (currentURL ?? app?.url) &&
        !queryParams.get('page')
      ) {
        setValue('isValidated', 'true', { shouldDirty: true });
      }
    }
  }, [watch('url'), currentURL]);

  useEffect(() => {
    if (watch('isValidated') && !!app?.authentication) {
      setCurrentURL(getValues('url'));
    }
  }, [watch('isValidated')]);

  return (
    <Box css={{ marginTop: '$40' }}>
      {loader ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ height: "calc(100vh - 73px - 160px)" }}
        >
          <Spinner size="md" />
        </Flex>
      ) : (
        <>
          <Flex alignContent="center" justifyContent="center">
            <Box css={{ maxWidth: 1000, width: '100%' }}>
              <form onSubmit={handleSubmit(webhookOnSubmitHandler)}>
                <CurrentWebhookConfig
                  currentConfig={currentConfig}
                  setValue={setValue}
                  getValues={getValues}
                  proceedHandler={(value) => setCurrentConfig(value)}
                  previousHandler={(value) => setCurrentConfig(value)}
                  register={register}
                  handleBtnLoader={() => setBtnLoader(true)}
                  btnLoader={btnLoader}
                  errors={errors}
                  handleSubmit={handleSubmit}
                  trigger={trigger}
                />
              </form>
            </Box>
          </Flex>
        </>
      )}
    </Box>
  );
};

const CurrentWebhookConfig = ({
  currentConfig,
  previousHandler,
  proceedHandler,
  getValues,
  setValue,
  register,
  handleBtnLoader,
  btnLoader,
  errors,
  handleSubmit,
  trigger
}: {
  currentConfig: string;
  proceedHandler: (value: string) => void;
  previousHandler: (value: string) => void;
  getValues: UseFormGetValues<WebhookStateValues>;
  setValue: UseFormSetValue<WebhookStateValues>;
  register: UseFormRegister<WebhookStateValues>;
  handleBtnLoader: () => void;
  btnLoader: boolean;
  errors: any;
  handleSubmit: UseFormHandleSubmit<any>;
  trigger: UseFormTrigger<WebhookStateValues>;
}) => {
  switch (currentConfig) {
    case 'appDetails':
      return (
        <AppDetails
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          register={register}
          handleSubmit={handleSubmit}
          proceedHandler={() => proceedHandler('appAuthentication')}
        />
      );
    case 'appAuthentication':
      return (
        <AppAuthentication
          previousHandler={() => previousHandler('appDetails')}
          proceedHandler={() => proceedHandler('appEventConfiguration')}
          register={register}
          setValue={setValue}
          getValues={getValues}
          trigger={trigger}
          errors={errors}
        />
      );
    case 'appEventConfiguration':
      return (
        <AppEventConfiguration
          previousHandler={() => previousHandler('appAuthentication')}
          handleBtnLoader={() => handleBtnLoader()}
          btnLoader={btnLoader}
          setValue={setValue}
          getValues={getValues}
        />
      );
    default:
      return (
        <AppDetails
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          handleSubmit={handleSubmit}
          register={register}
          proceedHandler={() => proceedHandler('appAuthentication')}
        />
      );
  }
};

export default AddWebhook;
